import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Loan, Customer, InventoryItem, useLoansStore, useCustomersStore, useInventoryStore } from '@/hooks/useSupabaseStore';
import { useShop } from '@/hooks/useShop';
import { shopCategories, shopWarehouses } from '@/lib/brand';

const statusConfigMap: Record<string, { label: string; cls: string }> = {
  active:   { label: 'Đang vay',     cls: 'bg-blue-100 text-blue-700' },
  overdue:  { label: 'Quá hạn',      cls: 'bg-red-100 text-red-700' },
  due_soon: { label: 'Sắp đến hạn',  cls: 'bg-amber-100 text-amber-700' },
  redeemed: { label: 'Đã chuộc',     cls: 'bg-emerald-100 text-emerald-700' },
  forfeited:{ label: 'Đã thanh lý',  cls: 'bg-gray-100 text-gray-600' },
  interest_paid:{ label: 'Đã đóng lãi', cls: 'bg-purple-100 text-purple-700' },
};
import PrintLoanTicket from '@/pages/loans/components/PrintLoanTicket';

function makeEmptyLoanForm(interestRate: number, assetCategory: string): Omit<Loan, 'id'> {
  return {
    customerId: '', customerName: '', customerPhone: '',
    assetName: '', assetCategory,
    loanAmount: 0, interestRate,
    startDate: new Date().toISOString().slice(0, 10),
    dueDate: '', status: 'active',
    warehouse: '',
  };
}

const emptyCustomerForm: Omit<Customer, 'id'> = {
  name: '', phone: '', idCard: '', address: '',
  createdAt: new Date().toISOString().slice(0, 10),
  totalLoans: 0, activeLoans: 0, totalBorrowed: 0,
};

export default function LoansCreatePage() {
  const navigate = useNavigate();
  const { addLoan, loans } = useLoansStore();
  const { customers, addCustomer } = useCustomersStore();
  const { addItem } = useInventoryStore();
  const { shop, saveShop } = useShop();
  const categories = shopCategories(shop);
  const warehouses = shopWarehouses(shop);

  const [loanForm, setLoanForm] = useState<Omit<Loan, 'id'>>(() => makeEmptyLoanForm(3, 'Vàng bạc'));
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showNewCategoryInline, setShowNewCategoryInline] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rate = shop?.defaultInterestRate;
    const firstCat = shopCategories(shop)[0] || '';
    setLoanForm(prev => {
      const nextRate = rate ?? prev.interestRate;
      const nextCat = prev.assetCategory || firstCat;
      if (nextRate === prev.interestRate && nextCat === prev.assetCategory) return prev;
      return { ...prev, interestRate: nextRate, assetCategory: nextCat };
    });
  }, [shop]);

  // Hạn trả mặc định = ngày vay + thời hạn trong Cài đặt (chỉ khi user chưa tự chọn)
  const dueDateTouched = useRef(false);
  useEffect(() => {
    if (dueDateTouched.current || !loanForm.startDate) return;
    const d = new Date(loanForm.startDate.slice(0, 10));
    if (Number.isNaN(d.getTime())) return;
    d.setDate(d.getDate() + (shop?.defaultLoanDays || 30));
    const iso = d.toISOString().slice(0, 10);
    setLoanForm(prev => (prev.dueDate ? prev : { ...prev, dueDate: iso }));
  }, [loanForm.startDate, shop]);

  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerInline, setShowNewCustomerInline] = useState(false);
  const [inlineCustomerForm, setInlineCustomerForm] = useState<Omit<Customer, 'id'>>(emptyCustomerForm);

  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [printLoan, setPrintLoan] = useState<Loan | null>(null);

  // Auto-format loan amount display with dot separators
  const [loanAmountStr, setLoanAmountStr] = useState('');

  useEffect(() => {
    if (loanForm.loanAmount === 0) {
      setLoanAmountStr('');
    } else {
      const currentNum = Number(loanAmountStr.replace(/\D/g, ''));
      if (currentNum !== loanForm.loanAmount) {
        setLoanAmountStr(loanForm.loanAmount.toLocaleString('vi-VN'));
      }
    }
  }, [loanForm.loanAmount, loanAmountStr]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false);
        setShowNewCategoryInline(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  const suggestedCustomers = useMemo(() => {
    if (customerSearch.trim()) return filteredCustomers;
    return [...customers].sort((a, b) => {
      const aLatest = loans
        .filter(l => l.customerId === a.id)
        .reduce((max, l) => Math.max(max, new Date(l.startDate).getTime()), 0);
      const bLatest = loans
        .filter(l => l.customerId === b.id)
        .reduce((max, l) => Math.max(max, new Date(l.startDate).getTime()), 0);
      return bLatest - aLatest;
    });
  }, [customers, loans, customerSearch, filteredCustomers]);

  const selectCustomer = (c: Customer) => {
    setLoanForm(prev => ({ ...prev, customerId: c.id, customerName: c.name, customerPhone: c.phone }));
    setCustomerSearch(c.name);
    setShowCustomerDropdown(false);
  };

  const handleCreateInlineCustomer = async () => {
    if (!inlineCustomerForm.name || !inlineCustomerForm.phone) {
      showToast('Vui lòng nhập tên và số điện thoại!');
      return;
    }
    const newId = `KH${Date.now()}`;
    const newCustomer: Customer = { ...inlineCustomerForm, id: newId };
    try {
      await addCustomer(newCustomer);
      setLoanForm(prev => ({ ...prev, customerId: newId, customerName: newCustomer.name, customerPhone: newCustomer.phone }));
      setCustomerSearch(newCustomer.name);
      setShowNewCustomerInline(false);
      setInlineCustomerForm(emptyCustomerForm);
      showToast('Đã tạo khách hàng mới!');
    } catch {
      showToast('Lỗi: Không thể tạo khách hàng. Vui lòng thử lại!');
    }
  };

  const handleAddLoan = async () => {
    if (!loanForm.customerName || !loanForm.assetName || !loanForm.loanAmount || !loanForm.dueDate) {
      showToast('Vui lòng điền đầy đủ thông tin!');
      return;
    }
    setSubmitting(true);
    const now = new Date();
    const datePart = `${String(now.getFullYear()).slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const seqPart = String(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()).padStart(5, '0');
    const newId = `KV${datePart}-${seqPart}`;
    const startDateTime = `${loanForm.startDate} ${startTime}`;
    const newLoan: Loan = { ...loanForm, startDate: startDateTime, id: newId };
    try {
      await addLoan(newLoan);

      const newItemId = `HH${Date.now()}`;
      const newItem: InventoryItem = {
        id: newItemId,
        name: loanForm.assetName,
        category: loanForm.assetCategory,
        description: '',
        estimatedValue: loanForm.loanAmount,
        loanAmount: loanForm.loanAmount,
        status: 'pawned',
        loanId: newId,
        customerName: loanForm.customerName,
        receivedDate: loanForm.startDate,
        condition: 'good',
        warehouse: loanForm.warehouse || '',
      };
      await addItem(newItem);

      showToast('Thêm khoản vay thành công!');
      dueDateTouched.current = false;
      setLoanForm(makeEmptyLoanForm(shop?.defaultInterestRate ?? 3, categories[0] || ''));
      setCustomerSearch('');
      setShowNewCustomerInline(false);
      const resetNow = new Date();
      setStartTime(`${String(resetNow.getHours()).padStart(2, '0')}:${String(resetNow.getMinutes()).padStart(2, '0')}`);

      setPrintLoan(newLoan);
    } catch {
      showToast('Lỗi: Không thể thêm khoản vay. Vui lòng thử lại!');
    } finally {
      setSubmitting(false);
    }
  };

  return (<>
      {toast && (
        <div className="fixed top-5 right-5 z-[100] bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg">
          {toast}
        </div>
      )}

      {printLoan && (
        <PrintLoanTicket
          loan={printLoan}
          customers={customers}
          onClose={() => { setPrintLoan(null); navigate('/loans'); }}
        />
      )}

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-[#f59e0b]/10 rounded-lg">
                <i className="ri-file-add-line text-[#f59e0b] text-base"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm">Tạo Khoản Vay Mới</h3>
                <p className="text-gray-400 text-xs">Nhập thông tin hợp đồng cầm đồ</p>
              </div>
            </div>
            <button onClick={() => navigate('/loans')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              Quay lại danh sách
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Chọn khách hàng */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Khách hàng *</label>
                <button
                  onClick={() => { setShowNewCustomerInline(!showNewCustomerInline); setInlineCustomerForm(emptyCustomerForm); }}
                  className="flex items-center gap-1 text-xs text-[#f59e0b] font-medium hover:underline cursor-pointer whitespace-nowrap">
                  <i className="ri-user-add-line"></i>
                  {showNewCustomerInline ? 'Chọn khách có sẵn' : 'Tạo khách hàng mới'}
                </button>
              </div>

              {!showNewCustomerInline ? (
                <div className="relative" ref={dropdownRef}>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400"><i className="ri-search-line text-sm"></i></span>
                    <input
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); setLoanForm(prev => ({ ...prev, customerId: '', customerName: e.target.value, customerPhone: '' })); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      placeholder="Tìm tên hoặc số điện thoại..."
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                    />
                  </div>
                  {showCustomerDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden z-20 max-h-64 overflow-y-auto">
                      {!customerSearch.trim() && (
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5">
                          <span className="w-3.5 h-3.5 flex items-center justify-center text-gray-400"><i className="ri-history-line text-xs"></i></span>
                          <p className="text-xs font-semibold text-gray-500">Khách hàng gần đây</p>
                        </div>
                      )}
                      {customerSearch.trim() && (
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                          <p className="text-xs font-semibold text-gray-500">Kết quả tìm kiếm</p>
                        </div>
                      )}
                      {suggestedCustomers.length > 0 ? suggestedCustomers.map(c => (
                        <button key={c.id} onClick={() => selectCustomer(c)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 transition-colors cursor-pointer text-left">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <span className="text-amber-700 font-bold text-xs">{c.name.charAt(0)}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800">{c.name}</p>
                            <p className="text-xs text-gray-400">{c.phone} · {c.id}</p>
                          </div>
                          {loans.some(l => l.customerId === c.id) && (
                            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0">
                              {loans.filter(l => l.customerId === c.id).length} khoản
                            </span>
                          )}
                        </button>
                      )) : (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">Không tìm thấy khách hàng</div>
                      )}
                    </div>
                  )}
                  {loanForm.customerId && (
                    <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <span className="w-4 h-4 flex items-center justify-center text-amber-600"><i className="ri-checkbox-circle-fill text-sm"></i></span>
                      <span className="text-xs text-amber-700 font-medium">{loanForm.customerName} · {loanForm.customerPhone}</span>
                    </div>
                  )}

                  {/* Khoản vay trước đây của khách */}
                  {loanForm.customerId && (
                    <div className="mt-3 bg-amber-50/60 border border-amber-100 rounded-xl p-4">
                      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-history-line"></i></span>
                        Khoản vay trước đây của {loanForm.customerName}
                      </p>
                      {(() => {
                        const customerLoans = loans
                          .filter(l => l.customerId === loanForm.customerId)
                          .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
                        if (customerLoans.length === 0) {
                          return <p className="text-xs text-gray-500">Khách hàng này chưa có khoản vay nào trước đây.</p>;
                        }
                        return (
                          <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                            {customerLoans.map(l => (
                              <button
                                key={l.id}
                                onClick={() => {
                                  setLoanForm(prev => ({
                                    ...prev,
                                    assetName: l.assetName,
                                    assetCategory: l.assetCategory,
                                    loanAmount: l.loanAmount,
                                    interestRate: l.interestRate,
                                  }));
                                  setLoanAmountStr(l.loanAmount.toLocaleString('vi-VN'));
                                  showToast(`Đã điền thông tin từ ${l.id}`);
                                }}
                                className="w-full text-left border border-amber-200/60 bg-white rounded-lg px-3 py-2.5 hover:bg-amber-50 transition-colors cursor-pointer"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-800 truncate">
                                      {l.assetName} <span className="text-gray-400 font-normal">· {l.assetCategory}</span>
                                    </p>
                                    <p className="text-[11px] text-gray-500 mt-0.5">
                                      {l.loanAmount.toLocaleString('vi-VN')}đ · {l.interestRate}%/tháng · {l.startDate.slice(0, 10)}
                                    </p>
                                  </div>
                                  <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusConfigMap[l.status]?.cls || 'bg-gray-100 text-gray-600'}`}>
                                    {statusConfigMap[l.status]?.label || l.status}
                                  </span>
                                </div>
                                <p className="text-[10px] text-amber-600 font-medium mt-1.5 flex items-center gap-1">
                                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-arrow-right-circle-line"></i></span>
                                  Nhấn để dùng lại thông tin
                                </p>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-dashed border-gray-300">
                  <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-amber-500"></i></span>
                    Tạo khách hàng mới
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 font-medium block mb-1">Họ tên *</label>
                      <input
                        value={inlineCustomerForm.name}
                        onChange={e => setInlineCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium block mb-1">Số điện thoại *</label>
                      <input
                        value={inlineCustomerForm.phone}
                        onChange={e => setInlineCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium block mb-1">CMND/CCCD</label>
                      <input
                        value={inlineCustomerForm.idCard}
                        onChange={e => setInlineCustomerForm(prev => ({ ...prev, idCard: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 font-medium block mb-1">Địa chỉ</label>
                      <input
                        value={inlineCustomerForm.address}
                        onChange={e => setInlineCustomerForm(prev => ({ ...prev, address: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white"
                      />
                    </div>
                  </div>
                  <button onClick={handleCreateInlineCustomer}
                    className="w-full py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap">
                    Tạo &amp; chọn khách hàng này
                  </button>
                </div>
              )}
            </div>

            {/* Thông tin khoản vay */}
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Thông tin khoản vay</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tên tài sản cầm cố *</label>
                  <input value={loanForm.assetName} onChange={e => setLoanForm(prev => ({ ...prev, assetName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Danh mục</label>
                  <div className="relative" ref={categoryRef}>
                    <input
                      value={loanForm.assetCategory}
                      onChange={e => {
                        setLoanForm(prev => ({ ...prev, assetCategory: e.target.value }));
                        setShowCategoryDropdown(true);
                        setShowNewCategoryInline(false);
                      }}
                      onFocus={() => setShowCategoryDropdown(true)}
                      placeholder="Chọn hoặc nhập danh mục..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]"
                    />
                    {showCategoryDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden z-20 max-h-48 overflow-y-auto">
                        {categories
                          .filter(c => c.toLowerCase().includes(loanForm.assetCategory.toLowerCase()))
                          .map(c => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => {
                                setLoanForm(prev => ({ ...prev, assetCategory: c }));
                                setShowCategoryDropdown(false);
                                setShowNewCategoryInline(false);
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-amber-50 transition-colors cursor-pointer ${loanForm.assetCategory === c ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-700'}`}
                            >
                              {c}
                            </button>
                          ))}
                        {!categories.some(c => c.toLowerCase() === loanForm.assetCategory.trim().toLowerCase()) && loanForm.assetCategory.trim() && !showNewCategoryInline && (
                          <button
                            type="button"
                            onClick={() => setShowNewCategoryInline(true)}
                            className="w-full px-4 py-2.5 text-left text-sm text-[#f59e0b] font-medium hover:bg-amber-50 transition-colors cursor-pointer flex items-center gap-1.5"
                          >
                            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
                            Thêm "{loanForm.assetCategory}"
                          </button>
                        )}
                        {showNewCategoryInline && (
                          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                            <div className="flex gap-2">
                              <input
                                value={newCategoryName}
                                onChange={e => setNewCategoryName(e.target.value)}
                                placeholder="Tên danh mục mới..."
                                className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#f59e0b] bg-white"
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (newCategoryName.trim()) {
                                      const trimmed = newCategoryName.trim();
                                      if (!categories.includes(trimmed)) {
                                        saveShop({ loanCategories: [...categories, trimmed] });
                                      }
                                      setLoanForm(prev => ({ ...prev, assetCategory: trimmed }));
                                      setNewCategoryName('');
                                      setShowNewCategoryInline(false);
                                      setShowCategoryDropdown(false);
                                    }
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (newCategoryName.trim()) {
                                    const trimmed = newCategoryName.trim();
                                    if (!categories.includes(trimmed)) {
                                      saveShop({ loanCategories: [...categories, trimmed] });
                                    }
                                    setLoanForm(prev => ({ ...prev, assetCategory: trimmed }));
                                    setNewCategoryName('');
                                    setShowNewCategoryInline(false);
                                    setShowCategoryDropdown(false);
                                  }
                                }}
                                className="px-3 py-1.5 bg-[#f59e0b] text-white rounded-lg text-xs font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap"
                              >
                                Lưu
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Giá trị vay (đ) *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={loanAmountStr}
                    onChange={e => {
                      const raw = e.target.value.replace(/\D/g, '');
                      const num = Number(raw) || 0;
                      setLoanAmountStr(raw ? Number(raw).toLocaleString('vi-VN') : '');
                      setLoanForm(prev => ({ ...prev, loanAmount: num }));
                    }}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[500000, 1000000, 2000000, 3000000, 5000000, 10000000, 15000000, 20000000, 30000000, 50000000].map(amt => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setLoanForm(prev => ({ ...prev, loanAmount: amt }))}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${loanForm.loanAmount === amt ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}
                      >
                        {amt >= 1000000 ? `${amt / 1000000}tr` : `${amt / 1000}k`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Lãi suất (%/tháng)</label>
                  <input type="number" step="0.5" value={loanForm.interestRate} onChange={e => setLoanForm(prev => ({ ...prev, interestRate: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                  <div className="flex gap-1.5 mt-2">
                    {[1, 1.5, 2, 2.5, 3, 5].map(rate => (
                      <button
                        key={rate}
                        type="button"
                        onClick={() => setLoanForm(prev => ({ ...prev, interestRate: rate }))}
                        className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${loanForm.interestRate === rate ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}
                      >
                        {rate}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Ngày vay *</label>
                  <input type="date" value={loanForm.startDate} onChange={e => setLoanForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Giờ vay</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
                      <i className="ri-time-line text-sm"></i>
                    </span>
                    <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    {['07:00', '08:00', '09:00', '10:00', '14:00', '15:00', '16:00'].map(t => (
                      <button key={t} type="button" onClick={() => setStartTime(t)}
                        className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${startTime === t ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Hạn trả *</label>
                  <input type="date" value={loanForm.dueDate} onChange={e => { dueDateTouched.current = true; setLoanForm(prev => ({ ...prev, dueDate: e.target.value })); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Kho</label>
                  <select
                    value={loanForm.warehouse}
                    onChange={e => setLoanForm(prev => ({ ...prev, warehouse: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white cursor-pointer"
                  >
                    <option value="">— Không kho</option>
                    {warehouses.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Summary preview */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Tóm tắt hợp đồng</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Khách hàng:</span>
                  <span className="font-medium text-gray-700">{loanForm.customerName || '---'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Tài sản:</span>
                  <span className="font-medium text-gray-700">{loanForm.assetName || '---'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Vốn cho vay:</span>
                  <span className="font-semibold text-gray-800">{loanForm.loanAmount ? loanForm.loanAmount.toLocaleString('vi-VN') + 'đ' : '---'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Lãi suất:</span>
                  <span className="font-medium text-gray-700">{loanForm.interestRate}%/tháng</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Ngày vay:</span>
                  <span className="font-medium text-gray-700">{loanForm.startDate} {startTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Hạn trả:</span>
                  <span className="font-semibold text-amber-600">{loanForm.dueDate || '---'}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button onClick={() => navigate('/loans')}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Hủy
              </button>
              <button onClick={handleAddLoan} disabled={submitting}
                className="flex-1 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Đang lưu...
                  </span>
                ) : 'Lưu khoản vay'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
